// SettingsContext.js - FIXED VERSION
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LAMBDA_BASE_URL } from 'constants';
import { useAuth } from './AuthContext';
import encryptedStorage from '../utils/encryptedStorage';
import { FAST_API_BASE_URL } from 'constants';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { currentDomain } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  
  const [currentDomainId, setCurrentDomainId] = useState(null);

  const getStorageKey = useCallback(() => {
    return 'current_settings';
  }, []);
  useEffect(() => {
    if (currentDomain?.domain_token !== currentDomainId) {
      setSettings(null);
      setCurrentDomainId(currentDomain?.domain_token);
      const storageKey = getStorageKey();
      encryptedStorage.removeItem(storageKey);
    }
  }, [currentDomain, currentDomainId, getStorageKey]);

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    if (!currentDomain?.domain_token) {
      setLoading(false);
      return;
    }

    const storageKey = getStorageKey();
    const now = Date.now();
    const cacheDuration = 5 * 60 * 1000;

    try {
      setLoading(true);
      setError(null);

      if (!forceRefresh && storageKey) {
        const cached = encryptedStorage.getItem(storageKey);
        
        if (cached && 
            cached.timestamp && 
            (now - cached.timestamp < cacheDuration) &&
            cached.domainId === currentDomain.domain_token) {
          
          setSettings(cached.data);
          setLastFetched(cached.timestamp);
          setLoading(false);
          return cached.data;
        }
      }

      const domainId = currentDomain.domain_token || encryptedStorage.getItem('domain_token');
      // console.log('Fetching settings for domain ID:', domainId);
      const apiUrl = LAMBDA_BASE_URL;
      // const apiUrl= FAST_API_BASE_URL
      
      const res = await axios.get(`${apiUrl}/settings`, {
        headers: {
          'Content-Type': 'application/json',
          'DOMAIN-TOKEN': domainId,
        },
      });

      if (res.status === 200) {
        const result = res.data?.result;
        if (!result) {
          throw new Error('Invalid API response format: missing result');
        }

        const chatCurlArray = result.ansera_search_chat_curl;
        if (!Array.isArray(chatCurlArray) || chatCurlArray.length === 0) {
          const emptySettings = formatSettings({});
          setSettings(emptySettings);
          setLastFetched(now);
          
          if (storageKey) {
            encryptedStorage.setItem(storageKey, {
              data: emptySettings,
              timestamp: now,
              domainId: currentDomain.domain_token,
            });
          }
          
          setLoading(false);
          return emptySettings;
        }

        const settingData = chatCurlArray[0] || {};
        const formattedSettings = formatSettings(settingData);
        
        setSettings(formattedSettings);
        setLastFetched(now);
        if (storageKey) {
          encryptedStorage.setItem(storageKey, {
            data: formattedSettings,
            timestamp: now,
            domainId: currentDomain.domain_token,
          });
        }

        setLoading(false);
        return formattedSettings;
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message || 'Failed to fetch settings');
      if (storageKey) {
        const cached = encryptedStorage.getItem(storageKey);
        if (cached && cached.domainId === currentDomain?.domain_token) {
          setSettings(cached.data);
          setLastFetched(cached.timestamp);
        }
      }
      
      setLoading(false);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentDomain, getStorageKey]);

  const updateSettings = useCallback(async (updates) => {
    if (!currentDomain?.domain_token || !settings) {
      throw new Error('No domain or settings available');
    }

    try {
      setLoading(true);
      const domainId = currentDomain.domain_token;
      const apiUrl = LAMBDA_BASE_URL;
      // const apiUrl= FAST_API_BASE_URL
      const payload = {};
      Object.keys(updates).forEach(key => {
        if (updates[key] !== settings[key]) {
          payload[key] = updates[key];
        }
      });

      if (Object.keys(payload).length === 0) {
        return settings; 
      }

      await axios.patch(`${apiUrl}/settings`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'DOMAIN-TOKEN': domainId,
        },
      });

      const updatedSettings = { ...settings, ...payload };
      setSettings(updatedSettings);

      const storageKey = getStorageKey();
      if (storageKey) {
        encryptedStorage.setItem(storageKey, {
          data: updatedSettings,
          timestamp: Date.now(),
          domainId: currentDomain.domain_token,
        });
      }

      setLastFetched(Date.now());
      setLoading(false);
      
      return updatedSettings;
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.message || 'Failed to update settings');
      setLoading(false);
      throw err;
    }
  }, [currentDomain, settings, getStorageKey]);

  const formatSettings = (rawSettings) => {
    if (!rawSettings) return null;

    return {
      ansera_search_display_default_questions: rawSettings.ansera_search_display_default_questions || 0,
      ansera_search_default_questions: rawSettings.ansera_search_default_questions 
        ? JSON.parse(rawSettings.ansera_search_default_questions)
        : ["", "", "", "", ""],
      
      ansera_search_google_recaptcha_v3_enable: rawSettings.ansera_search_google_recaptcha_v3_enable || 0,
      ansera_search_google_recaptcha_v3_key_id: rawSettings.ansera_search_google_recaptcha_v3_key_id || "",
      ansera_search_google_recaptcha_v3_key_secret: rawSettings.ansera_search_google_recaptcha_v3_key_secret || "",
      ansera_search_suspicious_message: rawSettings.ansera_search_suspicious_message || "",
      
      ansera_search_email_placeholder: rawSettings.ansera_search_email_placeholder || "",
      email_header: rawSettings.email_header || "",
      email_footer: rawSettings.email_footer || "",
      ansera_search_option3_widget: rawSettings.ansera_search_option3_widget || "",
      ansera_search_theme: rawSettings.ansera_search_theme || "",
      ansera_search_logo_url: rawSettings.ansera_search_logo_url || "",
      ansera_search_logo_height: rawSettings.ansera_search_logo_height || "",
      ansera_search_logo_width: rawSettings.ansera_search_logo_width || "",
      ansera_search_search_bar_text: rawSettings.ansera_search_search_bar_text || "",
      no_answer_text: rawSettings.no_answer_text || "",
      ansera_search_contact_us_text: rawSettings.ansera_search_contact_us_text || "",
      ansera_search_contact_us_url: rawSettings.ansera_search_contact_us_url || "",
      ansera_search_feedback_message: rawSettings.ansera_search_feedback_message || "",
      ansera_search_send_mail_text: rawSettings.ansera_search_send_mail_text || "",
      ansera_chat_header_text: rawSettings.ansera_chat_header_text || "",
      
      ansera_search_custom_bg_color: rawSettings.ansera_search_custom_bg_color || "",
      ansera_search_custom_text_color: rawSettings.ansera_search_custom_text_color || "",
      ansera_search_custom_button_bg_color: rawSettings.ansera_search_custom_button_bg_color || "",
      ansera_search_custom_button_hover_color: rawSettings.ansera_search_custom_button_hover_color || "",
      ansera_search_custom_input_border_color: rawSettings.ansera_search_custom_input_border_color || "",
      ansera_search_answer_bg_color: rawSettings.ansera_search_answer_bg_color || "",
      ansera_search_tiles_bg_color: rawSettings.ansera_search_tiles_bg_color || "",
      ansera_search_tiles_text_color: rawSettings.ansera_search_tiles_text_color || "",
      
      ansera_chat_bubble_background_color: rawSettings.ansera_chat_bubble_background_color || "",
      ansera_chat_pane_background_color: rawSettings.ansera_chat_pane_background_color || "",
      ansera_chat_pane_question_background_color: rawSettings.ansera_chat_pane_question_background_color || "",
      ansera_chat_pane_question_text_color: rawSettings.ansera_chat_pane_question_text_color || "",
      ansera_search_chat_header_bg_color: rawSettings.ansera_search_chat_header_bg_color || "",
      ansera_search_chat_header_text_color: rawSettings.ansera_search_chat_header_text_color || "",
      ansera_search_chat_footer_bg_color: rawSettings.ansera_search_chat_footer_bg_color || "",
      ansera_search_chat_footer_text_color: rawSettings.ansera_search_chat_footer_text_color || "",
      
      _raw: rawSettings,
    };
  };

  const refreshSettings = useCallback(() => {
    return fetchSettings(true);
  }, [fetchSettings]);

  const clearCache = useCallback(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      encryptedStorage.removeItem(storageKey);
    }
    setSettings(null);
    setLastFetched(null);
  }, [getStorageKey]);


  useEffect(() => {
    if (currentDomain) {
      fetchSettings();
    } else {
      setSettings(null);
      setLoading(false);
    }
  }, [currentDomain, fetchSettings]);

  useEffect(() => {
    if (!currentDomain) return;

    const intervalId = setInterval(() => {
      const cached = encryptedStorage.getItem(getStorageKey());
      if (cached && cached.domainId === currentDomain.domain_token) {
        fetchSettings();
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [currentDomain, fetchSettings, getStorageKey]);

  const value = {
    settings,
    loading,
    error,
    lastFetched,
    currentDomainId,
    
    fetchSettings,
    updateSettings,
    refreshSettings,
    clearCache,
    getSetting: (key) => settings?.[key],
    
    isReady: !!settings && !loading,
    hasSettings: !!settings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;